import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ── Types ─────────────────────────────────────────────────────────────────────

export type Device = 'desktop' | 'tablet' | 'mobile'

export interface BlockTypography {
  fontFamily?: string     // CSS font-family value
  fontWeight?: string     // '300' | '400' | '500' | '600' | '700' | '900'
  textAlign?: string      // 'left' | 'center' | 'right'
  letterSpacing?: string  // e.g. '0.05em'
  lineHeight?: string     // e.g. '1.6'
  headingSize?: string    // e.g. '48px' or '3rem'
  bodySize?: string       // e.g. '16px' or '1rem'
}

export interface BlockColors {
  bg?: string             // background color override
  textColor?: string      // main text color
  accentColor?: string    // accent / CTA color
}

export interface BlockButton {
  bg?: string             // e.g. '#5b6af0'
  color?: string          // e.g. '#ffffff'
  radius?: number         // px
  paddingSize?: 'sm' | 'md' | 'lg'
  borderColor?: string
}

// ── Visual effects (border, shadow, opacity, transforms, blend mode) ──────────
export interface BlockVisualStyles {
  borderRadius?: string        // '12px', '50%', '8px 16px 0 0'
  borderWidth?: string         // '1px'
  borderStyle?: string         // 'solid' | 'dashed' | 'dotted'
  borderColor?: string
  boxShadow?: string           // '0 4px 24px rgba(0,0,0,0.12)'
  opacity?: number             // 0–1
  backdropFilter?: string      // 'blur(16px)'
  mixBlendMode?: string        // 'multiply' | 'overlay' | 'screen' | ...
  rotate?: string              // '3deg'
  scale?: string               // '1.05'
  overflow?: 'hidden' | 'visible' | 'auto'
}

// ── Responsive overrides per breakpoint ───────────────────────────────────────
export interface BlockBreakpointOverride {
  wrapPaddingTop?: number
  wrapPaddingBottom?: number
  wrapPaddingLeft?: number
  wrapPaddingRight?: number
  headingSize?: string
  bodySize?: string
  blockMinHeight?: number
  hideBlock?: boolean
}

export interface BlockResponsiveStyles {
  tablet?: BlockBreakpointOverride
  mobile?: BlockBreakpointOverride
}

// ── Section layout engine (flex / grid) ───────────────────────────────────────
export interface SectionLayout {
  display?: 'block' | 'flex' | 'grid'
  flexDirection?: 'row' | 'column' | 'row-reverse'
  flexWrap?: 'wrap' | 'nowrap'
  gap?: string
  alignItems?: string
  justifyContent?: string
  gridCols?: string            // e.g. 'repeat(3, 1fr)'
}

// ── Phase E: Block-level layout, size, position, margin ──────────────────────
export interface BlockLayout {
  display?: 'block' | 'flex' | 'grid' | 'inline' | 'inline-block' | 'none'
  flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  flexWrap?: 'wrap' | 'nowrap'
  gap?: string
  alignItems?: string
  justifyContent?: string
  gridCols?: string
  gridRows?: string
  flexGrow?: number
  flexShrink?: number
  flexBasis?: string
}

export interface BlockSize {
  width?: string       // '100%', '320px', 'auto', etc.
  height?: string
  minWidth?: string
  maxWidth?: string
  minHeight?: string
  maxHeight?: string
  aspectRatio?: string // '16/9', '1/1'
}

export interface BlockPositionStyle {
  position?: 'static' | 'relative' | 'absolute' | 'sticky'
  top?: string
  right?: string
  bottom?: string
  left?: string
  zIndex?: number
}

export interface BlockMargin {
  top?: number
  right?: number
  bottom?: number
  left?: number
}

export interface BlockState {
  id: string
  type: string
  data: Record<string, unknown>
  abTestId?: string
  abVariant?: 'A' | 'B'
  customCss?: string
  customJs?: string
  hideOn?: ('desktop' | 'tablet' | 'mobile')[]
  hoverEffect?: string
  buttonEffect?: string
  blockStyle?: string
  blockTypography?: BlockTypography
  blockButton?: BlockButton
  blockColors?: BlockColors
  wrapPaddingTop?: number; wrapPaddingBottom?: number; wrapPaddingLeft?: number; wrapPaddingRight?: number
  wrapBackground?: string
  blockMinHeight?: number
  blockLinkUrl?: string
  blockLinkTarget?: '_blank' | '_self'
  // Visual effects & responsive overrides
  blockVisualStyles?: BlockVisualStyles
  blockResponsiveStyles?: BlockResponsiveStyles
  // Phase E: Layout, Size, Position, Margin
  blockLayout?: BlockLayout
  blockSize?: BlockSize
  blockPosition?: BlockPositionStyle
  blockMargin?: BlockMargin
}

export interface SectionState {
  id: string
  name?: string
  blocks: BlockState[]
  paddingTop: number
  paddingBottom: number
  background: string
  backgroundSize?: string
  backgroundPosition?: string
  backgroundVideo?: string
  bgOverlay?: string
  bgOverlayOpacity?: number
  fullHeight?: boolean
  minHeight?: number
  sectionAnimation?: string
  anchorId?: string
  sectionStyle?: string
  // New: layout engine
  sectionLayout?: SectionLayout
}

export interface PageState {
  id: string
  title: string
  slug: string
  seoTitle: string
  seoDesc: string
  ogImage: string
  robotsIndex: boolean
  canonicalUrl: string
  structuredData: string
  pageCss: string
  pageJs: string
  password?: string
  cmsTemplateFor?: string
  sections: SectionState[]
}

export interface SiteSettings {
  name: string
  favicon: string
  gaId: string
  ymId: string
  fbPixelId: string
  vkPixelId: string
  gtmId: string
  themeConfig: string
  customHeadHtml: string
  customBodyHtml: string
  formWebhookUrl: string
  formEmailTo: string
  formTelegramChatId: string
  customDomain: string
  customDomainVerified: boolean
  cookieConsent: boolean
  cookieConsentText: string
  mailchimpApiKey: string
  mailchimpListId: string
  unisenderApiKey: string
  unisenderListId: string
  sendpulseClientId: string
  sendpulseClientSecret: string
  sendpulseListId: string
  bitrix24WebhookUrl: string
  amoCrmDomain: string
  amoCrmToken: string
  getCourseDomain: string
  smsRuApiKey: string
  smsOwnerPhone: string
  whatsappNumber: string
  telegramUsername: string
  vkGroupId: string
  callbackPhone: string
  jivoChatId: string
  crispWebsiteId: string
  tawkPropertyId: string
  tawkWidgetId: string
  recaptchaSiteKey: string
  recaptchaSecretKey: string
  socialProofEnabled: boolean
  socialProofMessages: string
  backToTop: boolean
  scrollProgress: boolean
  scrollProgressColor: string
  pagePreloader: boolean
  customCursor: boolean
  customCursorColor: string
  pageTransition: string
  siteLanguage: string
  pwaEnabled: boolean
  pwaName: string
  pwaShortName: string
  pwaThemeColor: string
  pwaBackgroundColor: string
  maintenanceMode: boolean
  maintenanceText: string
  hideBstBadge: boolean
  additionalLanguages: string[]
  clarityProjectId: string
  hotjarSiteId: string
  plausibleDomain: string
  matomoUrl: string
  matomoSiteId: string
  googleVerification: string
  yandexVerification: string
  bingVerification: string
  publiclyListed: boolean
  publicListingTags: string[]
  designStyle: string
  aiApprovalLevel: 'auto' | 'ask_destructive' | 'always_ask'
}

// ── Store interface ───────────────────────────────────────────────────────────

interface EditorStore {
  // ── Data ─────────────────────────────────────────────────────────────────────
  page: PageState
  pages: PageState[]
  activePageId: string
  settings: SiteSettings
  globalHeader: SectionState | null
  globalFooter: SectionState | null

  // ── UI state ─────────────────────────────────────────────────────────────────
  selectedBlockId: string | null
  selectedSectionId: string | null
  editingBlockId: string | null
  device: Device
  zoom: number
  showGrid: boolean
  sidebarPanel: 'blocks' | 'layers' | 'pages'
  settingsOpen: boolean
  history: PageState[]
  historyIndex: number
  saveStatus: 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
  saveError: string | null
  setSaveStatus: (status: 'idle' | 'dirty' | 'saving' | 'saved' | 'error', error?: string | null) => void
  isReadOnly: boolean
  setReadOnly: (v: boolean) => void
  userPlan: string
  setUserPlan: (plan: string) => void

  // ── Lazy page loading ────────────────────────────────────────────────────────
  loadedPageIds: string[]
  setPageContent: (pageId: string, sections: SectionState[]) => void

  // ── Project Engine routing ────────────────────────────────────────────────────
  projectId: string | null
  setProjectId: (id: string) => void
  replacePageId: (localId: string, serverId: string) => void

  // ── Job tracking (§88) ───────────────────────────────────────────────────────
  activeJobs: Array<{ jobId: string; label: string }>
  trackJob: (jobId: string, label: string) => void
  removeJob: (jobId: string) => void

  // ── Selection ────────────────────────────────────────────────────────────────
  selectBlock: (id: string | null) => void
  selectSection: (id: string | null) => void
  setEditingBlock: (id: string | null) => void
  setDevice: (device: Device) => void
  setZoom: (zoom: number) => void
  toggleGrid: () => void
  setSidebarPanel: (panel: 'blocks' | 'layers' | 'pages') => void
  setSettingsOpen: (open: boolean) => void

  // ── Pages ────────────────────────────────────────────────────────────────────
  addPage: (title?: string) => void
  deletePage: (id: string) => void
  setActivePage: (id: string) => void
  renamePage: (id: string, title: string) => void
  duplicatePage: (id: string) => void
  updatePage: (patch: Partial<Pick<PageState, 'title' | 'slug' | 'seoTitle' | 'seoDesc' | 'ogImage' | 'robotsIndex' | 'canonicalUrl' | 'structuredData' | 'pageCss' | 'pageJs' | 'password' | 'cmsTemplateFor'>>) => void
  replacePageSections: (pageId: string, sections: SectionState[]) => void

  // ── Settings ─────────────────────────────────────────────────────────────────
  updateSettings: (patch: Partial<SiteSettings>) => void

  // ── Init from API ─────────────────────────────────────────────────────────────
  initFromApiSite: (site: {
    id: string; name: string; slug: string; status: string
    favicon?: string; gaId?: string; ymId?: string; fbPixelId?: string; vkPixelId?: string; gtmId?: string
    themeConfig?: string; customHeadHtml?: string; customBodyHtml?: string; formWebhookUrl?: string; formEmailTo?: string
    customDomain?: string; customDomainVerified?: boolean; globalHeader?: string; globalFooter?: string
    cookieConsent?: boolean; cookieConsentText?: string
    formTelegramChatId?: string
    mailchimpApiKey?: string; mailchimpListId?: string
    unisenderApiKey?: string; unisenderListId?: string
    sendpulseClientId?: string; sendpulseClientSecret?: string; sendpulseListId?: string
    bitrix24WebhookUrl?: string; amoCrmDomain?: string; amoCrmToken?: string; getCourseDomain?: string; smsRuApiKey?: string; smsOwnerPhone?: string
    whatsappNumber?: string; telegramUsername?: string; vkGroupId?: string; callbackPhone?: string
    jivoChatId?: string; crispWebsiteId?: string; tawkPropertyId?: string; tawkWidgetId?: string
    recaptchaSiteKey?: string
    socialProofEnabled?: boolean; socialProofMessages?: string; backToTop?: boolean
    scrollProgress?: boolean; scrollProgressColor?: string; pagePreloader?: boolean; customCursor?: boolean; customCursorColor?: string; pageTransition?: string; siteLanguage?: string
    pwaEnabled?: boolean; pwaName?: string; pwaShortName?: string; pwaThemeColor?: string; pwaBackgroundColor?: string
    maintenanceMode?: boolean; maintenanceText?: string; hideBstBadge?: boolean
    additionalLanguages?: string[]
    clarityProjectId?: string; hotjarSiteId?: string; plausibleDomain?: string; matomoUrl?: string; matomoSiteId?: string
    googleVerification?: string; yandexVerification?: string; bingVerification?: string
    publiclyListed?: boolean; publicListingTags?: string[]
    pages?: Array<{
      id: string; title: string; slug: string; isHome: boolean
      seoTitle?: string; seoDesc?: string; ogImage?: string; structuredData?: string; robotsIndex?: boolean; canonicalUrl?: string
      pageCss?: string; pageJs?: string; cmsTemplateFor?: string
      content?: { sections?: unknown[] }
    }>
  }) => void

  // ── Global sections ──────────────────────────────────────────────────────────
  toggleGlobalHeader: () => void
  toggleGlobalFooter: () => void
  updateGlobalSection: (target: 'header' | 'footer', patch: Partial<Pick<SectionState, 'background' | 'paddingTop' | 'paddingBottom'>>) => void

  // ── Sections ─────────────────────────────────────────────────────────────────
  addSection: () => void
  removeSection: (id: string) => void
  duplicateSection: (id: string) => void
  updateSection: (id: string, patch: Partial<SectionState>) => void
  moveSectionUp: (id: string) => void
  moveSectionDown: (id: string) => void

  // ── Phase E: Design System tokens ────────────────────────────────────────────
  designTokens: import('../utils/api').ApiDesignToken[]
  setDesignTokens: (tokens: import('../utils/api').ApiDesignToken[]) => void

  // ── Phase E: Block style extensions ──────────────────────────────────────────
  setBlockLayout: (sectionId: string, blockId: string, layout: BlockLayout | undefined) => void
  setBlockSize: (sectionId: string, blockId: string, size: BlockSize | undefined) => void
  setBlockPosition: (sectionId: string, blockId: string, pos: BlockPositionStyle | undefined) => void
  setBlockMargin: (sectionId: string, blockId: string, margin: BlockMargin | undefined) => void

  // ── Blocks ───────────────────────────────────────────────────────────────────
  addBlock: (sectionId: string, type: string, defaultData: Record<string, unknown>) => void
  pasteBlock: (sectionId: string, block: Omit<BlockState, 'id'>) => void
  removeBlock: (sectionId: string, blockId: string) => void
  updateBlock: (sectionId: string, blockId: string, data: Record<string, unknown>) => void
  setBlockAbTest: (sectionId: string, blockId: string, abTestId: string, abVariant: 'A' | 'B' | '') => void
  setBlockCustomCode: (sectionId: string, blockId: string, customCss: string, customJs: string) => void
  setBlockHideOn: (sectionId: string, blockId: string, hideOn: ('desktop' | 'tablet' | 'mobile')[]) => void
  setBlockHoverEffect: (sectionId: string, blockId: string, effect: string) => void
  setBlockButtonEffect: (sectionId: string, blockId: string, effect: string) => void
  setBlockStyle: (sectionId: string, blockId: string, style: string) => void
  setBlockTypography: (sectionId: string, blockId: string, typo: BlockTypography | undefined) => void
  setBlockButton: (sectionId: string, blockId: string, btn: BlockButton | undefined) => void
  setBlockColors: (sectionId: string, blockId: string, colors: BlockColors | undefined) => void
  setBlockWrapper: (sectionId: string, blockId: string, patch: Partial<Pick<BlockState, 'wrapPaddingTop' | 'wrapPaddingBottom' | 'wrapPaddingLeft' | 'wrapPaddingRight' | 'wrapBackground' | 'blockMinHeight' | 'blockLinkUrl' | 'blockLinkTarget' | 'blockVisualStyles' | 'blockResponsiveStyles'>>) => void
  duplicateBlock: (sectionId: string, blockId: string) => void
  moveBlock: (fromSection: string, blockId: string, toSection: string, toIndex: number) => void

  // ── History ──────────────────────────────────────────────────────────────────
  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

let _counter = 0
function uid(): string {
  return `bst-${Date.now()}-${++_counter}`
}

function mkPage(title = 'Новая страница', slug = '/new-page'): PageState {
  return {
    id: uid(),
    title,
    slug,
    seoTitle: '',
    seoDesc: '',
    ogImage: '',
    robotsIndex: true,
    canonicalUrl: '',
    structuredData: '',
    pageCss: '',
    pageJs: '',
    sections: [],
  }
}

function mkSection(): SectionState {
  return { id: uid(), blocks: [], paddingTop: 80, paddingBottom: 80, background: 'transparent' }
}

const GLOBAL_HEADER_ID = '__global_header__'
const GLOBAL_FOOTER_ID = '__global_footer__'

function isGlobalId(sectionId: string) {
  return sectionId === GLOBAL_HEADER_ID || sectionId === GLOBAL_FOOTER_ID
}

const INITIAL_PAGE = mkPage('Главная страница', '/')

const INITIAL_SETTINGS: SiteSettings = {
  name: 'Мой сайт',
  favicon: '',
  gaId: '',
  ymId: '',
  fbPixelId: '',
  vkPixelId: '',
  gtmId: '',
  themeConfig: '',
  customHeadHtml: '',
  customBodyHtml: '',
  formWebhookUrl: '',
  formEmailTo: '',
  formTelegramChatId: '',
  customDomain: '',
  customDomainVerified: false,
  cookieConsent: false,
  cookieConsentText: '',
  mailchimpApiKey: '',
  mailchimpListId: '',
  unisenderApiKey: '',
  unisenderListId: '',
  sendpulseClientId: '',
  sendpulseClientSecret: '',
  sendpulseListId: '',
  bitrix24WebhookUrl: '',
  amoCrmDomain: '',
  amoCrmToken: '',
  getCourseDomain: '',
  smsRuApiKey: '',
  smsOwnerPhone: '',
  whatsappNumber: '',
  telegramUsername: '',
  vkGroupId: '',
  callbackPhone: '',
  jivoChatId: '',
  crispWebsiteId: '',
  tawkPropertyId: '',
  tawkWidgetId: '',
  recaptchaSiteKey: '',
  recaptchaSecretKey: '',
  socialProofEnabled: false,
  socialProofMessages: '',
  backToTop: false,
  scrollProgress: false,
  scrollProgressColor: '#5b6af0',
  pagePreloader: false,
  customCursor: false,
  customCursorColor: '#5b6af0',
  pageTransition: '',
  siteLanguage: '',
  pwaEnabled: false,
  pwaName: '',
  pwaShortName: '',
  pwaThemeColor: '#5b6af0',
  pwaBackgroundColor: '#ffffff',
  maintenanceMode: false,
  maintenanceText: '',
  hideBstBadge: false,
  additionalLanguages: [],
  clarityProjectId: '',
  hotjarSiteId: '',
  plausibleDomain: '',
  matomoUrl: '',
  matomoSiteId: '',
  googleVerification: '',
  yandexVerification: '',
  bingVerification: '',
  publiclyListed: false,
  publicListingTags: [],
  designStyle: '',
  aiApprovalLevel: 'ask_destructive',
}

const MAX_HISTORY = 50
const MAX_CACHED_PAGES = 10

type StoreSlice = Pick<EditorStore, 'page' | 'pages' | 'activePageId' | 'history' | 'historyIndex'>

function syncPages(state: StoreSlice, nextPage: PageState): { page: PageState; pages: PageState[] } {
  return {
    page: nextPage,
    pages: state.pages.map((p) => (p.id === state.activePageId ? nextPage : p)),
  }
}

function withHistory(state: StoreSlice, nextPage: PageState): Partial<EditorStore> {
  const history = [...state.history.slice(0, state.historyIndex + 1), state.page].slice(-MAX_HISTORY)
  return {
    ...syncPages(state, nextPage),
    history,
    historyIndex: history.length - 1,
  }
}

function updateGlobalBlocks(
  state: Pick<EditorStore, 'globalHeader' | 'globalFooter'>,
  sectionId: string,
  mapFn: (blocks: BlockState[]) => BlockState[],
): Partial<EditorStore> | null {
  if (sectionId === GLOBAL_HEADER_ID && state.globalHeader) {
    return { globalHeader: { ...state.globalHeader, blocks: mapFn(state.globalHeader.blocks) } }
  }
  if (sectionId === GLOBAL_FOOTER_ID && state.globalFooter) {
    return { globalFooter: { ...state.globalFooter, blocks: mapFn(state.globalFooter.blocks) } }
  }
  return null
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useEditorStore = create<EditorStore>()(
  persist(
    (set, get) => ({
      page: INITIAL_PAGE,
      pages: [INITIAL_PAGE],
      activePageId: INITIAL_PAGE.id,
      settings: INITIAL_SETTINGS,
      globalHeader: null,
      globalFooter: null,

      selectedBlockId: null,
      selectedSectionId: null,
      editingBlockId: null,
      device: 'desktop',
      zoom: 100,
      showGrid: false,
      sidebarPanel: 'blocks',
      settingsOpen: false,
      history: [],
      historyIndex: -1,
      saveStatus: 'idle',
      saveError: null,
      isReadOnly: false,
      userPlan: 'FREE',
      loadedPageIds: [],
      projectId: null,
      setProjectId: (id) => set({ projectId: id }),
      replacePageId: (localId, serverId) => {
        const s = get()
        set({
          pages: s.pages.map(p => p.id === localId ? { ...p, id: serverId } : p),
          activePageId: s.activePageId === localId ? serverId : s.activePageId,
          page: s.page.id === localId ? { ...s.page, id: serverId } : s.page,
          loadedPageIds: s.loadedPageIds.map(id => id === localId ? serverId : id),
          // Keep undo history consistent: entries that reference the old ID must point to the server ID
          history: s.history.map(h => h.id === localId ? { ...h, id: serverId } : h),
        })
      },
      activeJobs: [],
      trackJob: (jobId, label) => set(s => ({ activeJobs: [...s.activeJobs.filter(j => j.jobId !== jobId), { jobId, label }] })),
      removeJob: (jobId) => set(s => ({ activeJobs: s.activeJobs.filter(j => j.jobId !== jobId) })),

      // ── Phase E: Design System ───────────────────────────────────────────────
      designTokens: [],
      setDesignTokens: (tokens) => set({ designTokens: tokens }),
      setPageContent: (pageId, sections) => {
        const s = get()
        const alreadyLoaded = s.loadedPageIds.includes(pageId)
        let nextLoadedIds = alreadyLoaded ? s.loadedPageIds : [...s.loadedPageIds, pageId]
        let updatedPages = s.pages.map(p => p.id === pageId ? { ...p, sections } : p)

        // LRU eviction: evict oldest non-active page when cache exceeds limit
        if (!alreadyLoaded && nextLoadedIds.length > MAX_CACHED_PAGES) {
          const evictId = nextLoadedIds.find(id => id !== s.activePageId && id !== pageId)
          if (evictId) {
            nextLoadedIds = nextLoadedIds.filter(id => id !== evictId)
            updatedPages = updatedPages.map(p => p.id === evictId ? { ...p, sections: [] } : p)
          }
        }

        if (s.activePageId === pageId) {
          set({ pages: updatedPages, page: { ...s.page, sections }, loadedPageIds: nextLoadedIds })
        } else {
          set({ pages: updatedPages, loadedPageIds: nextLoadedIds })
        }
      },

      // ── Selection ────────────────────────────────────────────────────────────
      selectBlock: (id) => set({ selectedBlockId: id, selectedSectionId: null, editingBlockId: null }),
      selectSection: (id) => set({ selectedSectionId: id, selectedBlockId: null, editingBlockId: null }),
      setEditingBlock: (id) => set({ editingBlockId: id }),
      setDevice: (device) => set({ device }),
      setZoom: (zoom) => set({ zoom: Math.max(25, Math.min(200, zoom)) }),
      toggleGrid: () => set((s) => ({ showGrid: !s.showGrid })),
      setSidebarPanel: (panel) => set({ sidebarPanel: panel }),
      setSettingsOpen: (open) => set({ settingsOpen: open }),
      setSaveStatus: (status, error = null) => set({ saveStatus: status, saveError: error ?? null }),
      setReadOnly: (v) => set({ isReadOnly: v }),
      setUserPlan: (plan) => set({ userPlan: plan }),

      // ── Pages ────────────────────────────────────────────────────────────────
      addPage: (title) => {
        const s = get()
        const updatedPages = s.pages.map((p) => (p.id === s.activePageId ? s.page : p))
        const slug = `/${(title ?? 'page').toLowerCase().replace(/\s+/g, '-')}-${Date.now().toString(36)}`
        const newPage = mkPage(title ?? 'Новая страница', slug)
        set({
          pages: [...updatedPages, newPage],
          page: newPage,
          activePageId: newPage.id,
          loadedPageIds: [...s.loadedPageIds, newPage.id],
          history: [],
          historyIndex: -1,
          selectedBlockId: null,
          selectedSectionId: null,
        })
      },

      deletePage: (id) => {
        const s = get()
        if (s.pages.length <= 1) return
        const nextPages = s.pages.filter((p) => p.id !== id)
        const nextActiveId = s.activePageId === id ? nextPages[0].id : s.activePageId
        const nextPage = nextPages.find((p) => p.id === nextActiveId) ?? nextPages[0]
        set({
          pages: nextPages,
          page: nextPage,
          activePageId: nextActiveId,
          loadedPageIds: s.loadedPageIds.filter(pid => pid !== id),
          history: [],
          historyIndex: -1,
          selectedBlockId: null,
          selectedSectionId: null,
        })
      },

      setActivePage: (id) => {
        const s = get()
        if (s.activePageId === id) return
        const updatedPages = s.pages.map((p) => (p.id === s.activePageId ? s.page : p))
        const targetPage = updatedPages.find((p) => p.id === id)
        if (!targetPage) return
        set({
          pages: updatedPages,
          page: targetPage,
          activePageId: id,
          history: [],
          historyIndex: -1,
          selectedBlockId: null,
          selectedSectionId: null,
        })
      },

      renamePage: (id, title) => {
        const s = get()
        const nextPages = s.pages.map((p) => (p.id === id ? { ...p, title } : p))
        const nextPage = s.activePageId === id ? { ...s.page, title } : s.page
        set({ pages: nextPages, page: nextPage })
      },

      duplicatePage: (id) => {
        const s = get()
        const src = s.pages.find((p) => p.id === id) ?? (s.activePageId === id ? s.page : undefined)
        if (!src) return
        const copy: PageState = {
          ...JSON.parse(JSON.stringify(src)) as PageState,
          id: uid(),
          title: `${src.title} (копия)`,
          slug: `${src.slug}-copy`,
        }
        const updatedPages = s.pages.map((p) => (p.id === s.activePageId ? s.page : p))
        const srcIdx = updatedPages.findIndex((p) => p.id === id)
        const nextPages = [...updatedPages.slice(0, srcIdx + 1), copy, ...updatedPages.slice(srcIdx + 1)]
        set({
          pages: nextPages,
          page: copy,
          activePageId: copy.id,
          loadedPageIds: [...s.loadedPageIds, copy.id],
          history: [],
          historyIndex: -1,
          selectedBlockId: null,
          selectedSectionId: null,
        })
      },

      updatePage: (patch) => {
        const s = get()
        const nextPage = { ...s.page, ...patch }
        set(syncPages(s, nextPage))
      },

      replacePageSections: (pageId, sections) => {
        const s = get()
        const target = s.pages.find(p => p.id === pageId)
        if (!target) return
        const updatedTarget = { ...target, sections }
        const updatedPages = s.pages.map(p => p.id === pageId ? updatedTarget : p)
        if (s.activePageId === pageId) {
          // It's the active page — push to history so Ctrl+Z works
          const history = [...s.history.slice(0, s.historyIndex + 1), s.page].slice(-MAX_HISTORY)
          set({ page: updatedTarget, pages: updatedPages, history, historyIndex: history.length - 1, selectedBlockId: null, selectedSectionId: null })
        } else {
          set({ pages: updatedPages })
        }
      },

      // ── Settings ──────────────────────────────────────────────────────────────
      updateSettings: (patch) => {
        const s = get()
        set({ settings: { ...s.settings, ...patch } })
      },

      // ── Init from API ─────────────────────────────────────────────────────────
      initFromApiSite: (site) => {
        const rawPages = site.pages ?? []
        const mappedPages: PageState[] = rawPages.map(p => ({
          id: p.id,
          title: p.title,
          slug: p.slug,
          seoTitle: p.seoTitle ?? '',
          seoDesc: p.seoDesc ?? '',
          ogImage: p.ogImage ?? '',
          robotsIndex: p.robotsIndex ?? true,
          canonicalUrl: p.canonicalUrl ?? '',
          structuredData: p.structuredData ?? '',
          pageCss: p.pageCss ?? '',
          pageJs: p.pageJs ?? '',
          cmsTemplateFor: p.cmsTemplateFor ?? undefined,
          sections: (p.content?.sections ?? []) as SectionState[],
        }))
        // Mark all pages with content as loaded; pages without content will be lazy-loaded later
        const loadedPageIds = mappedPages
          .filter(p => p.sections.length > 0)
          .map(p => p.id)
        const firstPage = mappedPages[0] ?? mkPage('Главная страница', '/')
        const settings: SiteSettings = {
          name: site.name,
          favicon: site.favicon ?? '',
          gaId: site.gaId ?? '',
          ymId: site.ymId ?? '',
          fbPixelId: site.fbPixelId ?? '',
          vkPixelId: site.vkPixelId ?? '',
          gtmId: site.gtmId ?? '',
          themeConfig: site.themeConfig ?? '',
          customHeadHtml: site.customHeadHtml ?? '',
          customBodyHtml: site.customBodyHtml ?? '',
          formWebhookUrl: site.formWebhookUrl ?? '',
          formEmailTo: site.formEmailTo ?? '',
          formTelegramChatId: site.formTelegramChatId ?? '',
          customDomain: site.customDomain ?? '',
          customDomainVerified: site.customDomainVerified ?? false,
          cookieConsent: site.cookieConsent ?? false,
          cookieConsentText: site.cookieConsentText ?? '',
          mailchimpApiKey: site.mailchimpApiKey ?? '',
          mailchimpListId: site.mailchimpListId ?? '',
          unisenderApiKey: site.unisenderApiKey ?? '',
          unisenderListId: site.unisenderListId ?? '',
          sendpulseClientId: site.sendpulseClientId ?? '',
          sendpulseClientSecret: site.sendpulseClientSecret ?? '',
          sendpulseListId: site.sendpulseListId ?? '',
          bitrix24WebhookUrl: site.bitrix24WebhookUrl ?? '',
          amoCrmDomain: site.amoCrmDomain ?? '',
          amoCrmToken: site.amoCrmToken ?? '',
          getCourseDomain: site.getCourseDomain ?? '',
          smsRuApiKey: site.smsRuApiKey ?? '',
          smsOwnerPhone: site.smsOwnerPhone ?? '',
          whatsappNumber: site.whatsappNumber ?? '',
          telegramUsername: site.telegramUsername ?? '',
          vkGroupId: site.vkGroupId ?? '',
          callbackPhone: site.callbackPhone ?? '',
          jivoChatId: site.jivoChatId ?? '',
          crispWebsiteId: site.crispWebsiteId ?? '',
          tawkPropertyId: site.tawkPropertyId ?? '',
          tawkWidgetId: site.tawkWidgetId ?? '',
          recaptchaSiteKey: site.recaptchaSiteKey ?? '',
          recaptchaSecretKey: '',
          socialProofEnabled: site.socialProofEnabled ?? false,
          socialProofMessages: site.socialProofMessages ?? '',
          backToTop: site.backToTop ?? false,
          scrollProgress: site.scrollProgress ?? false,
          scrollProgressColor: site.scrollProgressColor ?? '#5b6af0',
          pagePreloader: site.pagePreloader ?? false,
          customCursor: site.customCursor ?? false,
          customCursorColor: site.customCursorColor ?? '#5b6af0',
          pageTransition: site.pageTransition ?? '',
          siteLanguage: site.siteLanguage ?? '',
          pwaEnabled: site.pwaEnabled ?? false,
          pwaName: site.pwaName ?? '',
          pwaShortName: site.pwaShortName ?? '',
          pwaThemeColor: site.pwaThemeColor ?? '#5b6af0',
          pwaBackgroundColor: site.pwaBackgroundColor ?? '#ffffff',
          maintenanceMode: site.maintenanceMode ?? false,
          maintenanceText: site.maintenanceText ?? '',
          hideBstBadge: site.hideBstBadge ?? false,
          additionalLanguages: site.additionalLanguages ?? [],
          clarityProjectId: site.clarityProjectId ?? '',
          hotjarSiteId: site.hotjarSiteId ?? '',
          plausibleDomain: site.plausibleDomain ?? '',
          matomoUrl: site.matomoUrl ?? '',
          matomoSiteId: site.matomoSiteId ?? '',
          googleVerification: site.googleVerification ?? '',
          yandexVerification: site.yandexVerification ?? '',
          bingVerification: site.bingVerification ?? '',
          publiclyListed: site.publiclyListed ?? false,
          publicListingTags: site.publicListingTags ?? [],
          designStyle: '',
          aiApprovalLevel: 'ask_destructive' as const,
        }
        let globalHeader: SectionState | null = null
        let globalFooter: SectionState | null = null
        if (site.globalHeader) {
          try { globalHeader = JSON.parse(site.globalHeader) as SectionState } catch { /* ignore */ }
        }
        if (site.globalFooter) {
          try { globalFooter = JSON.parse(site.globalFooter) as SectionState } catch { /* ignore */ }
        }
        localStorage.setItem('bst-site-id', site.id)
        localStorage.setItem('bst-site-slug', site.slug)
        // Pages with sections are loaded; others will be lazy-fetched by usePageLazyLoad
        const allLoadedIds = loadedPageIds
        set({
          pages: mappedPages.length > 0 ? mappedPages : [firstPage],
          page: firstPage,
          activePageId: firstPage.id,
          loadedPageIds: allLoadedIds,
          settings,
          globalHeader,
          globalFooter,
          history: [],
          historyIndex: -1,
          selectedBlockId: null,
          selectedSectionId: null,
          editingBlockId: null,
        })
      },

      // ── Global sections ───────────────────────────────────────────────────────
      toggleGlobalHeader: () => {
        const s = get()
        if (s.globalHeader) {
          set({ globalHeader: null, selectedBlockId: null })
        } else {
          const sec = mkSection()
          sec.paddingTop = 0
          sec.paddingBottom = 0
          set({ globalHeader: sec })
        }
      },

      toggleGlobalFooter: () => {
        const s = get()
        if (s.globalFooter) {
          set({ globalFooter: null, selectedBlockId: null })
        } else {
          const sec = mkSection()
          sec.paddingTop = 0
          sec.paddingBottom = 0
          set({ globalFooter: sec })
        }
      },

      updateGlobalSection: (target, patch) => {
        const s = get()
        if (target === 'header' && s.globalHeader) {
          set({ globalHeader: { ...s.globalHeader, ...patch } })
        } else if (target === 'footer' && s.globalFooter) {
          set({ globalFooter: { ...s.globalFooter, ...patch } })
        }
      },

      // ── Sections ─────────────────────────────────────────────────────────────
      addSection: () => {
        const s = get()
        const section = mkSection()
        const nextPage = { ...s.page, sections: [...s.page.sections, section] }
        set({ ...withHistory(s, nextPage), selectedSectionId: section.id })
      },

      removeSection: (id) => {
        const s = get()
        const nextPage = { ...s.page, sections: s.page.sections.filter((sec) => sec.id !== id) }
        set({ ...withHistory(s, nextPage), selectedBlockId: null, selectedSectionId: null })
      },

      updateSection: (id, patch) => {
        const s = get()
        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) => (sec.id === id ? { ...sec, ...patch } : sec)),
        }
        set(withHistory(s, nextPage))
      },

      moveSectionUp: (id) => {
        const s = get()
        const idx = s.page.sections.findIndex((sec) => sec.id === id)
        if (idx <= 0) return
        const sections = [...s.page.sections]
        ;[sections[idx - 1], sections[idx]] = [sections[idx], sections[idx - 1]]
        set(withHistory(s, { ...s.page, sections }))
      },

      moveSectionDown: (id) => {
        const s = get()
        const idx = s.page.sections.findIndex((sec) => sec.id === id)
        if (idx < 0 || idx >= s.page.sections.length - 1) return
        const sections = [...s.page.sections]
        ;[sections[idx], sections[idx + 1]] = [sections[idx + 1], sections[idx]]
        set(withHistory(s, { ...s.page, sections }))
      },

      duplicateSection: (id) => {
        const s = get()
        const idx = s.page.sections.findIndex((sec) => sec.id === id)
        if (idx < 0) return
        const original = s.page.sections[idx]
        const clone: SectionState = {
          ...original,
          id: uid(),
          blocks: original.blocks.map((b) => ({ ...b, id: uid() })),
        }
        const sections = [...s.page.sections]
        sections.splice(idx + 1, 0, clone)
        set({ ...withHistory(s, { ...s.page, sections }), selectedSectionId: clone.id })
      },

      // ── Blocks ───────────────────────────────────────────────────────────────
      addBlock: (sectionId, type, defaultData) => {
        const s = get()
        const block: BlockState = { id: uid(), type, data: { ...defaultData } }

        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) => [...blocks, block])
          if (patch) set({ ...patch, selectedBlockId: block.id })
          return
        }

        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId ? { ...sec, blocks: [...sec.blocks, block] } : sec,
          ),
        }
        set({ ...withHistory(s, nextPage), selectedBlockId: block.id })
      },

      pasteBlock: (sectionId, blockData) => {
        const s = get()
        const block: BlockState = { ...blockData, id: uid() }
        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) => [...blocks, block])
          if (patch) set({ ...patch, selectedBlockId: block.id })
          return
        }
        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId ? { ...sec, blocks: [...sec.blocks, block] } : sec,
          ),
        }
        set({ ...withHistory(s, nextPage), selectedBlockId: block.id })
      },

      duplicateBlock: (sectionId, blockId) => {
        const s = get()

        if (isGlobalId(sectionId)) {
          const src = (sectionId === GLOBAL_HEADER_ID ? s.globalHeader : s.globalFooter)?.blocks.find(b => b.id === blockId)
          if (!src) return
          const copy: BlockState = { id: uid(), type: src.type, data: { ...src.data } }
          const patch = updateGlobalBlocks(s, sectionId, (blocks) => {
            const idx = blocks.findIndex(b => b.id === blockId)
            return [...blocks.slice(0, idx + 1), copy, ...blocks.slice(idx + 1)]
          })
          if (patch) set({ ...patch, selectedBlockId: copy.id })
          return
        }

        const sec = s.page.sections.find((sec) => sec.id === sectionId)
        if (!sec) return
        const block = sec.blocks.find((b) => b.id === blockId)
        if (!block) return
        const copy: BlockState = { id: uid(), type: block.type, data: { ...block.data } }
        const idx = sec.blocks.findIndex((b) => b.id === blockId)
        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? { ...sec, blocks: [...sec.blocks.slice(0, idx + 1), copy, ...sec.blocks.slice(idx + 1)] }
              : sec,
          ),
        }
        set({ ...withHistory(s, nextPage), selectedBlockId: copy.id })
      },

      removeBlock: (sectionId, blockId) => {
        const s = get()

        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) => blocks.filter(b => b.id !== blockId))
          if (patch) set({ ...patch, selectedBlockId: s.selectedBlockId === blockId ? null : s.selectedBlockId })
          return
        }

        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? { ...sec, blocks: sec.blocks.filter((b) => b.id !== blockId) }
              : sec,
          ),
        }
        set({
          ...withHistory(s, nextPage),
          selectedBlockId: s.selectedBlockId === blockId ? null : s.selectedBlockId,
        })
      },

      updateBlock: (sectionId, blockId, data) => {
        const s = get()

        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, data: { ...b.data, ...data } } : b)
          )
          if (patch) set(patch)
          return
        }

        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? {
                  ...sec,
                  blocks: sec.blocks.map((b) =>
                    b.id === blockId ? { ...b, data: { ...b.data, ...data } } : b,
                  ),
                }
              : sec,
          ),
        }
        set(withHistory(s, nextPage))
      },

      setBlockAbTest: (sectionId, blockId, abTestId, abVariant) => {
        const s = get()

        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId
              ? { ...b, abTestId: abTestId || undefined, abVariant: (abVariant as 'A' | 'B') || undefined }
              : b
            )
          )
          if (patch) set(patch)
          return
        }

        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? {
                  ...sec,
                  blocks: sec.blocks.map((b) =>
                    b.id === blockId
                      ? { ...b, abTestId: abTestId || undefined, abVariant: (abVariant as 'A' | 'B') || undefined }
                      : b,
                  ),
                }
              : sec,
          ),
        }
        set(withHistory(s, nextPage))
      },

      setBlockCustomCode: (sectionId, blockId, customCss, customJs) => {
        const s = get()

        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId
              ? { ...b, customCss: customCss || undefined, customJs: customJs || undefined }
              : b
            )
          )
          if (patch) set(patch)
          return
        }

        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? {
                  ...sec,
                  blocks: sec.blocks.map((b) =>
                    b.id === blockId ? { ...b, customCss: customCss || undefined, customJs: customJs || undefined } : b,
                  ),
                }
              : sec,
          ),
        }
        set(withHistory(s, nextPage))
      },

      setBlockHideOn: (sectionId, blockId, hideOn) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, hideOn: hideOn.length ? hideOn : undefined } : b)
          )
          if (patch) set(patch)
          return
        }
        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? { ...sec, blocks: sec.blocks.map((b) => b.id === blockId ? { ...b, hideOn: hideOn.length ? hideOn : undefined } : b) }
              : sec,
          ),
        }
        set(withHistory(s, nextPage))
      },

      setBlockHoverEffect: (sectionId, blockId, effect) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, hoverEffect: effect || undefined } : b)
          )
          if (patch) set(patch)
          return
        }
        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? { ...sec, blocks: sec.blocks.map((b) => b.id === blockId ? { ...b, hoverEffect: effect || undefined } : b) }
              : sec,
          ),
        }
        set(withHistory(s, nextPage))
      },

      setBlockButtonEffect: (sectionId, blockId, effect) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, buttonEffect: effect || undefined } : b)
          )
          if (patch) set(patch)
          return
        }
        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? { ...sec, blocks: sec.blocks.map((b) => b.id === blockId ? { ...b, buttonEffect: effect || undefined } : b) }
              : sec,
          ),
        }
        set(withHistory(s, nextPage))
      },

      setBlockStyle: (sectionId, blockId, style) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const patch = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, blockStyle: style || undefined } : b)
          )
          if (patch) set(patch)
          return
        }
        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? { ...sec, blocks: sec.blocks.map((b) => b.id === blockId ? { ...b, blockStyle: style || undefined } : b) }
              : sec,
          ),
        }
        set(withHistory(s, nextPage))
      },

      setBlockTypography: (sectionId, blockId, typo) => {
        const s = get()
        const upd = (b: BlockState) => b.id === blockId ? { ...b, blockTypography: typo } : b
        if (isGlobalId(sectionId)) {
          const p = updateGlobalBlocks(s, sectionId, (blocks) => blocks.map(upd))
          if (p) set(p)
          return
        }
        set(withHistory(s, { ...s.page, sections: s.page.sections.map(sec => sec.id === sectionId ? { ...sec, blocks: sec.blocks.map(upd) } : sec) }))
      },

      setBlockButton: (sectionId, blockId, btn) => {
        const s = get()
        const upd = (b: BlockState) => b.id === blockId ? { ...b, blockButton: btn } : b
        if (isGlobalId(sectionId)) {
          const p = updateGlobalBlocks(s, sectionId, (blocks) => blocks.map(upd))
          if (p) set(p)
          return
        }
        set(withHistory(s, { ...s.page, sections: s.page.sections.map(sec => sec.id === sectionId ? { ...sec, blocks: sec.blocks.map(upd) } : sec) }))
      },

      setBlockColors: (sectionId, blockId, colors) => {
        const s = get()
        const upd = (b: BlockState) => b.id === blockId ? { ...b, blockColors: colors } : b
        if (isGlobalId(sectionId)) {
          const p = updateGlobalBlocks(s, sectionId, (blocks) => blocks.map(upd))
          if (p) set(p)
          return
        }
        set(withHistory(s, { ...s.page, sections: s.page.sections.map(sec => sec.id === sectionId ? { ...sec, blocks: sec.blocks.map(upd) } : sec) }))
      },

      setBlockLayout: (sectionId, blockId, layout) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const p = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, blockLayout: layout } : b)
          )
          if (p) set(p)
          return
        }
        set(withHistory(s, { ...s.page, sections: s.page.sections.map(sec =>
          sec.id === sectionId ? { ...sec, blocks: sec.blocks.map(b => b.id === blockId ? { ...b, blockLayout: layout } : b) } : sec
        )}))
      },

      setBlockSize: (sectionId, blockId, size) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const p = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, blockSize: size } : b)
          )
          if (p) set(p)
          return
        }
        set(withHistory(s, { ...s.page, sections: s.page.sections.map(sec =>
          sec.id === sectionId ? { ...sec, blocks: sec.blocks.map(b => b.id === blockId ? { ...b, blockSize: size } : b) } : sec
        )}))
      },

      setBlockPosition: (sectionId, blockId, pos) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const p = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, blockPosition: pos } : b)
          )
          if (p) set(p)
          return
        }
        set(withHistory(s, { ...s.page, sections: s.page.sections.map(sec =>
          sec.id === sectionId ? { ...sec, blocks: sec.blocks.map(b => b.id === blockId ? { ...b, blockPosition: pos } : b) } : sec
        )}))
      },

      setBlockMargin: (sectionId, blockId, margin) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const p = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, blockMargin: margin } : b)
          )
          if (p) set(p)
          return
        }
        set(withHistory(s, { ...s.page, sections: s.page.sections.map(sec =>
          sec.id === sectionId ? { ...sec, blocks: sec.blocks.map(b => b.id === blockId ? { ...b, blockMargin: margin } : b) } : sec
        )}))
      },

      setBlockWrapper: (sectionId, blockId, patch) => {
        const s = get()
        if (isGlobalId(sectionId)) {
          const p = updateGlobalBlocks(s, sectionId, (blocks) =>
            blocks.map(b => b.id === blockId ? { ...b, ...patch } : b)
          )
          if (p) set(p)
          return
        }
        const nextPage = {
          ...s.page,
          sections: s.page.sections.map((sec) =>
            sec.id === sectionId
              ? { ...sec, blocks: sec.blocks.map((b) => b.id === blockId ? { ...b, ...patch } : b) }
              : sec,
          ),
        }
        set(withHistory(s, nextPage))
      },

      moveBlock: (fromSection, blockId, toSection, toIndex) => {
        const s = get()
        let block: BlockState | undefined
        let sections = s.page.sections.map((sec) => {
          if (sec.id === fromSection) {
            block = sec.blocks.find((b) => b.id === blockId)
            return { ...sec, blocks: sec.blocks.filter((b) => b.id !== blockId) }
          }
          return sec
        })
        if (!block) return
        const b = block
        sections = sections.map((sec) => {
          if (sec.id === toSection) {
            const blocks = [...sec.blocks]
            blocks.splice(toIndex, 0, b)
            return { ...sec, blocks }
          }
          return sec
        })
        set(withHistory(s, { ...s.page, sections }))
      },

      // ── History ──────────────────────────────────────────────────────────────
      undo: () => {
        const { history, historyIndex, activePageId } = get()
        if (historyIndex < 0) return
        const prevPage = history[historyIndex]
        set({
          page: prevPage,
          pages: get().pages.map((p) => (p.id === activePageId ? prevPage : p)),
          historyIndex: historyIndex - 1,
        })
      },

      redo: () => {
        const s = get()
        if (s.historyIndex >= s.history.length - 1) return
        const nextIndex = s.historyIndex + 1
        const nextPage = s.history[nextIndex]
        const history = [...s.history.slice(0, nextIndex), s.page]
        set({
          page: nextPage,
          pages: s.pages.map((p) => (p.id === s.activePageId ? nextPage : p)),
          history,
          historyIndex: nextIndex,
        })
      },

      canUndo: () => get().historyIndex >= 0,
      canRedo: () => get().historyIndex < get().history.length - 1,
    }),
    {
      name: 'bst-editor-v2',
      partialize: (state) => ({
        pages: state.pages,
        activePageId: state.activePageId,
        page: state.page,
        settings: state.settings,
        globalHeader: state.globalHeader,
        globalFooter: state.globalFooter,
      }),
    },
  ),
)

// ── Selectors ─────────────────────────────────────────────────────────────────

export const useSelectedBlock = () => {
  const { page, selectedBlockId, globalHeader, globalFooter } = useEditorStore()
  if (!selectedBlockId) return null

  // Check global sections first (sectionId signals routing to global actions)
  if (globalHeader) {
    const block = globalHeader.blocks.find((b) => b.id === selectedBlockId)
    if (block) return { block, sectionId: '__global_header__' }
  }
  if (globalFooter) {
    const block = globalFooter.blocks.find((b) => b.id === selectedBlockId)
    if (block) return { block, sectionId: '__global_footer__' }
  }

  for (const sec of page.sections) {
    const block = sec.blocks.find((b) => b.id === selectedBlockId)
    if (block) return { block, sectionId: sec.id }
  }
  return null
}
